/**
 * Pasted pseudo-lists, turned into real ones.
 *
 * A deliberate mirror of normaliseAuthoredLists in
 * backend/utils/report-helpers.js, so the preview shows the list the document
 * will print rather than the bullet-glyph paragraphs the author pasted.
 * verify-report-bands.js compares the two against the same inputs.
 *
 * Keep it dependency-free: the harness evaluates this file as plain text with
 * `export ` stripped, so an import statement here is a syntax error there.
 */

/**
 * Turn paragraphs that only LOOK like list items into real ones.
 *
 * Content pasted from Word arrives as ordinary paragraphs that begin with a
 * literal bullet glyph and a run of non-breaking spaces:
 *
 *   <p>&bull;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; The environment tested is ...</p>
 *
 * The editor's paste handler keeps the characters and drops Word's list
 * markup, so nothing downstream knows it is a list. Every renderer then does
 * the only thing it can: lay it out as a paragraph, which puts the glyph on
 * the left margin and wraps the second line underneath the glyph rather than
 * under the text. No amount of list styling reaches it, because there is no
 * list.
 *
 * Rewriting it into a real <ul> here means the three renderers' existing list
 * handling -- the hanging indent in Word and the PDF, the restored indent in
 * the preview -- applies to it unchanged.
 *
 * Deliberately conservative. Only a true bullet GLYPH counts, never a hyphen
 * or an asterisk, which start ordinary sentences. A numbered run has to begin
 * at 1 and count up, so a paragraph opening '2026. A year of ...' is left
 * alone.
 */
export const PSEUDO_BULLET_CHARS = '•·▪▫●○◦‣∙■';
export const PSEUDO_GAP = '(?:\\s|&nbsp;|&#160;|&#xa0;)';

/** Plain text of an HTML fragment, for deciding what a paragraph starts with. */
export function fragmentText(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
}

/** Drop a leading marker and the gap after it, leaving any tags in place. */
export function stripLeadingMarker(inner, markerRe) {
  // The marker may sit inside a <span> Word wrapped it in, so leading tags are
  // carried over rather than removed -- dropping an opening tag without its
  // closing one would unbalance the fragment.
  let out = String(inner).replace(markerRe, '$1');
  let prev;
  do {
    prev = out;
    out = out.replace(new RegExp('^((?:<[^>]*>)*)' + PSEUDO_GAP + '+'), '$1');
  } while (out !== prev);
  return out;
}

export function normaliseAuthoredLists(html) {
  const src = String(html || '');
  if (!src) return src;
  // Nothing to do for content that is already a list, or has no paragraphs.
  if (!/<p[\s>]/i.test(src)) return src;

  const bulletRe = new RegExp('^((?:\\s|<[^>]*>)*)[' + PSEUDO_BULLET_CHARS + ']');
  const bulletText = new RegExp('^\\s*[' + PSEUDO_BULLET_CHARS + ']\\s');
  const numberText = /^\s*(\d{1,3})[.)]\s/;

  // Split into paragraphs and the material between them, order preserved.
  const parts = [];
  const re = /<p(\s[^>]*)?>([\s\S]*?)<\/p>/gi;
  let last = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) parts.push({ kind: 'raw', text: src.slice(last, m.index) });
    parts.push({ kind: 'p', attrs: m[1] || '', inner: m[2], whole: m[0] });
    last = re.lastIndex;
  }
  if (last < src.length) parts.push({ kind: 'raw', text: src.slice(last) });
  if (!parts.some(p => p.kind === 'p')) return src;

  // Classify each paragraph.
  for (const part of parts) {
    if (part.kind !== 'p') continue;
    const text = fragmentText(part.inner);
    if (bulletText.test(text) && fragmentText(stripLeadingMarker(part.inner, bulletRe)).trim()) {
      part.marker = 'ul';
      continue;
    }
    const num = text.match(numberText);
    if (num) part.number = Number(num[1]);
  }

  // A numbered run only counts when it starts at 1 and climbs by one. That is
  // what separates a pasted list from a paragraph that opens with a year.
  let expect = 1;
  let runStart = -1;
  const markNumbered = (from, to) => { for (let i = from; i <= to; i++) if (parts[i].number != null) parts[i].marker = 'ol'; };
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p.kind === 'raw' && !p.text.trim()) continue;
    if (p.kind === 'p' && p.number === expect) {
      if (expect === 1) runStart = i;
      expect += 1;
      if (expect > 2 && i === parts.length - 1) markNumbered(runStart, i);
      continue;
    }
    // The run ended. Two or more items is a list; a lone '1.' is a sentence.
    if (expect > 2) markNumbered(runStart, i - 1);
    expect = (p.kind === 'p' && p.number === 1) ? 2 : 1;
    runStart = (p.kind === 'p' && p.number === 1) ? i : -1;
  }
  if (expect > 2 && runStart >= 0) markNumbered(runStart, parts.length - 1);

  if (!parts.some(p => p.marker)) return src;

  // Rebuild, grouping consecutive items of the same kind into one list.
  let out = '';
  let open = null;
  const close = () => { if (open) { out += `</${open}>`; open = null; } };
  for (const part of parts) {
    if (part.kind === 'raw') {
      // Whitespace between two items must not break the list in two.
      if (open && !part.text.trim()) continue;
      close();
      out += part.text;
      continue;
    }
    if (!part.marker) { close(); out += part.whole; continue; }
    if (open !== part.marker) { close(); open = part.marker; out += `<${open}>`; }
    const re2 = part.marker === 'ul'
      ? bulletRe
      : new RegExp('^((?:\\s|<[^>]*>)*)\\d{1,3}[.)]');
    out += `<li><p${part.attrs}>${stripLeadingMarker(part.inner, re2)}</p></li>`;
  }
  close();
  return out;
}
